import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import { getSeekerProfile } from '../services/seekerProfileService';
import { getCompanyProfileByUserId } from '../services/companyService';
import { getUserSubscriptions, updateSubscription, createSubscription, SUBSCRIPTION_STATUS } from '../services/subscriptionService';
import { getAllPackages, PACKAGE_TYPES } from '../services/packageService';

const SuperAdminPageApproval = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [userSubscriptions, setUserSubscriptions] = useState([]);
    const [availablePackages, setAvailablePackages] = useState([]);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [filter, setFilter] = useState('all'); // 'all', 'jobseeker', 'employer'
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchUsers();
        fetchPackages();
    }, []);

    const fetchUsers = async () => {
        try {
            const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            const usersList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setUsers(usersList);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPackages = async () => {
        try {
            const result = await getAllPackages();
            if (result.success) {
                setAvailablePackages(result.packages);
            }
        } catch (error) {
            console.error('Error fetching packages:', error);
        }
    };

    const handleUserSelect = async (user) => {
        setSelectedUser(user);
        setProfileData(null);
        setUserSubscriptions([]);
        setLoadingProfile(true);

        try {
            // Fetch Profile
            if (user.userType === 'jobseeker') {
                const result = await getSeekerProfile(user.id);
                if (result.success) {
                    setProfileData(result.profile);
                }
            } else if (user.userType === 'employer') {
                const result = await getCompanyProfileByUserId(user.id);
                if (result.success) {
                    setProfileData(result.data);
                }
            }

            // Fetch Subscriptions
            const subResult = await getUserSubscriptions(user.id);
            if (subResult.success) {
                setUserSubscriptions(subResult.subscriptions);
            }

        } catch (error) {
            console.error('Error fetching user data:', error);
        } finally {
            setLoadingProfile(false);
        }
    };

    const togglePageApproval = async () => {
        if (!selectedUser || !profileData) return;

        const collectionName = selectedUser.userType === 'jobseeker' ? 'seekerProfiles' : 'companies';
        const docId = selectedUser.userType === 'jobseeker' ? selectedUser.id : selectedUser.id;

        try {
            const docRef = doc(db, collectionName, docId);
            const newStatus = !profileData.isApproved;

            await updateDoc(docRef, {
                isApproved: newStatus
            });

            setProfileData(prev => ({
                ...prev,
                isApproved: newStatus
            }));

            alert(`Page ${newStatus ? 'Approved' : 'Unapproved'} successfully!`);
        } catch (error) {
            console.error('Error updating approval status:', error);
            alert('Failed to update status');
        }
    };

    const handleApproveSubscription = async (subscriptionId, currentStatus) => {
        if (!window.confirm('Are you sure you want to approve/activate this subscription?')) return;

        try {
            await updateSubscription(subscriptionId, {
                status: SUBSCRIPTION_STATUS.ACTIVE
            });

            // Update local state
            setUserSubscriptions(prev => prev.map(sub =>
                sub.id === subscriptionId ? { ...sub, status: SUBSCRIPTION_STATUS.ACTIVE } : sub
            ));

            alert('Subscription activated successfully!');
        } catch (error) {
            console.error('Error activating subscription:', error);
            alert('Failed to activate subscription');
        }
    };

    const handleAssignPackage = async (pkg) => {
        if (!selectedUser) return;
        if (!window.confirm(`Are you sure you want to assign "${pkg.name}" to ${selectedUser.firstName}? This will create an ACTIVE subscription.`)) return;

        try {
            const subscriptionData = {
                userId: selectedUser.id,
                userName: `${selectedUser.firstName} ${selectedUser.lastName}`,
                userPhone: selectedUser.phone,
                packageId: pkg.id,
                packageName: pkg.name,
                packageType: pkg.type,
                price: pkg.price,
                duration: pkg.duration,
                features: pkg.features || [],
                maxApplications: pkg.maxApplications || 0,
                maxJobPostings: pkg.maxJobPostings || 0,
                featuredJobs: pkg.featuredJobs || 0,
                resumeTemplates: pkg.resumeTemplates || 0,
                profileBoost: pkg.profileBoost || false,
                candidateSearch: pkg.candidateSearch || false,
                analyticsAccess: pkg.analyticsAccess || false,
                userType: selectedUser.userType,
                paymentId: 'manual_admin_approval',
                razorpayPaymentId: 'manual_admin_approval'
            };

            const result = await createSubscription(subscriptionData);

            if (result.success) {
                alert('Package assigned and activated successfully!');
                // Refresh subscriptions
                const subResult = await getUserSubscriptions(selectedUser.id);
                if (subResult.success) {
                    setUserSubscriptions(subResult.subscriptions);
                }
            } else {
                alert('Failed to assign package: ' + result.error);
            }
        } catch (error) {
            console.error('Error assigning package:', error);
            alert('Error assigning package');
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesFilter = filter === 'all' || user.userType === filter;
        const matchesSearch = (user.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (user.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    // Filter available packages based on selected user type
    const relevantPackages = availablePackages.filter(pkg => {
        if (!selectedUser) return false;
        if (selectedUser.userType === 'jobseeker') return pkg.type === PACKAGE_TYPES.JOB_SEEKER;
        if (selectedUser.userType === 'employer') return pkg.type === PACKAGE_TYPES.EMPLOYER;
        return false;
    });

    if (loading) return <LoadingSpinner />;

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Sidebar List */}
            <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold mb-4">Approvals Dashboard</h2>

                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-3 py-1 rounded-full text-sm ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilter('jobseeker')}
                            className={`px-3 py-1 rounded-full text-sm ${filter === 'jobseeker' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                        >
                            Seekers
                        </button>
                        <button
                            onClick={() => setFilter('employer')}
                            className={`px-3 py-1 rounded-full text-sm ${filter === 'employer' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                        >
                            Employers
                        </button>
                    </div>

                    <input
                        type="text"
                        placeholder="Search users..."
                        className="w-full px-3 py-2 border rounded-lg"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="flex-1 overflow-y-auto">
                    {filteredUsers.map(user => (
                        <div
                            key={user.id}
                            onClick={() => handleUserSelect(user)}
                            className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${selectedUser?.id === user.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''}`}
                        >
                            <div className="font-medium">{user.firstName} {user.lastName}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                            <div className="text-xs mt-1">
                                <span className={`px-2 py-0.5 rounded-full ${user.userType === 'employer' ? 'bg-green-100 text-green-800' :
                                        user.userType === 'jobseeker' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100'
                                    }`}>
                                    {user.userType === 'employer' ? 'Company' : user.userType}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto p-8">
                {selectedUser ? (
                    loadingProfile ? (
                        <LoadingSpinner />
                    ) : (
                        <div className="space-y-8">
                            {/* Profile Approval Section */}
                            {profileData ? (
                                <div className="bg-white rounded-lg shadow-lg p-6">
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <h2 className="text-2xl font-bold mb-2">
                                                {selectedUser.userType === 'employer' ? profileData.companyName : `${selectedUser.firstName} ${selectedUser.lastName}`}
                                            </h2>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${profileData.isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {profileData.isApproved ? 'Page Approved' : 'Page Approval Pending'}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={togglePageApproval}
                                            className={`px-6 py-2 rounded-lg font-medium text-white transition-colors ${profileData.isApproved
                                                    ? 'bg-red-600 hover:bg-red-700'
                                                    : 'bg-green-600 hover:bg-green-700'
                                                }`}
                                        >
                                            {profileData.isApproved ? 'Revoke Approval' : 'Approve Page'}
                                        </button>
                                    </div>

                                    {/* Profile Details */}
                                    <div className="space-y-6">
                                        {selectedUser.userType === 'employer' ? (
                                            <>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-sm text-gray-500">Industry</label>
                                                        <p className="font-medium">{profileData.industry}</p>
                                                    </div>
                                                    <div>
                                                        <label className="text-sm text-gray-500">Location</label>
                                                        <p className="font-medium">{profileData.location}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm text-gray-500">Description</label>
                                                    <p className="mt-1 text-gray-700">{profileData.description}</p>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-sm text-gray-500">Location</label>
                                                        <p className="font-medium">{profileData.personalInfo?.location || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <label className="text-sm text-gray-500">Experience</label>
                                                        <p className="font-medium">{profileData.experienceLevel || 'N/A'}</p>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                                    No profile data found.
                                </div>
                            )}

                            {/* Existing Subscriptions Section */}
                            <div className="bg-white rounded-lg shadow-lg p-6">
                                <h3 className="text-xl font-bold mb-4">Existing Subscriptions</h3>

                                {userSubscriptions.length > 0 ? (
                                    <div className="space-y-4">
                                        {userSubscriptions.map(sub => (
                                            <div key={sub.id} className="border rounded-lg p-4 flex justify-between items-center bg-gray-50">
                                                <div>
                                                    <h4 className="font-bold text-lg">{sub.packageName}</h4>
                                                    <div className="text-sm text-gray-600 space-y-1">
                                                        <p>Price: ₹{sub.price}</p>
                                                        <p>Duration: {sub.duration} days</p>
                                                        <p>Status:
                                                            <span className={`ml-2 font-medium ${sub.status === 'active' ? 'text-green-600' :
                                                                    sub.status === 'expired' ? 'text-red-600' : 'text-yellow-600'
                                                                }`}>
                                                                {sub.status?.toUpperCase()}
                                                            </span>
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            Purchased: {sub.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div>
                                                    {sub.status !== 'active' && (
                                                        <button
                                                            onClick={() => handleApproveSubscription(sub.id, sub.status)}
                                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm"
                                                        >
                                                            Approve / Activate
                                                        </button>
                                                    )}
                                                    {sub.status === 'active' && (
                                                        <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-medium text-sm border border-green-200">
                                                            Active
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No subscriptions found for this user.</p>
                                    </div>
                                )}
                            </div>

                            {/* Assign New Package Section */}
                            <div className="bg-white rounded-lg shadow-lg p-6">
                                <h3 className="text-xl font-bold mb-4">Assign New Package (Manual Approval)</h3>
                                <p className="text-sm text-gray-500 mb-4">Select a package to manually assign to this user. This will create an active subscription immediately.</p>

                                {relevantPackages.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {relevantPackages.map(pkg => (
                                            <div key={pkg.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-bold text-lg">{pkg.name}</h4>
                                                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                                        ₹{pkg.price}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{pkg.description}</p>
                                                <button
                                                    onClick={() => handleAssignPackage(pkg)}
                                                    className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-sm"
                                                >
                                                    Assign & Approve
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No compatible packages found for this user type.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                        </svg>
                        <p className="text-xl font-medium">Select a user to review</p>
                        <p>Choose a user to manage their profile and packages.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuperAdminPageApproval;
